import { type SupplierTeamMember } from '@choco-admin/types';

import { useSuppliersPageGetSupplierUsersQuery } from '../../../../../../../generated';
import { gqlRoleToSupplierUserRole } from '../../../../../../../utils/supplierRole';

// Backend caps `Supplier.users(first:)` at 200 and the connection exposes no
// pageInfo, so we request the max and flag truncation rather than silently
// under-report.
const TEAM_PAGE_SIZE = 200;

export const useSupplierTeam = (supplierId?: string) => {
  const { data, loading, error, refetch } =
    useSuppliersPageGetSupplierUsersQuery({
      variables: { id: supplierId ?? '', first: TEAM_PAGE_SIZE },
      skip: !supplierId,
      fetchPolicy: 'cache-and-network',
      // Downgrade after the first fetch so a cache write from an unrelated
      // query doesn't force a network refetch here unless the cache read is
      // genuinely incomplete (see the ProfileImage merge policy in
      // apollo-client.ts for the specific bug this used to trigger).
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    });

  const edges = data?.supplier?.users?.edges || [];

  const teamMembers: SupplierTeamMember[] = edges
    // The supplier's system bot account is indexed under the same
    // supplierId as human members and has no place in a "team" list.
    .filter((edge) => !edge.node.botUser)
    .map((edge) => ({
      id: edge.node.id,
      name: edge.node.name,
      email: edge.node.email,
      phone: edge.node.phone,
      // Backend exposes `role: [Role]`, but a supplier user holds exactly one.
      role: gqlRoleToSupplierUserRole(edge.node.role?.[0]),
      profileImage: edge.node.profileImage,
    }));

  return {
    teamMembers,
    // A full page of raw edges (before filtering) likely means more members
    // exist beyond what we fetched.
    isTruncated: edges.length >= TEAM_PAGE_SIZE,
    isLoading: loading,
    error,
    refetch,
  };
};
